import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { FaEdit, FaTrash, FaSave, FaTimes } from "react-icons/fa";
import { Alert, Spinner, Button, Modal } from "react-bootstrap";
import "./MedicationList.css";

const MedicationList = () => {
  const [medications, setMedications] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedMedication, setEditedMedication] = useState({
    name: "",
    dosage: "",
    expirationDate: "",
    quantity: "",
    condition: "",
    category: "",
    description: ""
  });
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "success" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [medToDelete, setMedToDelete] = useState(null);

  useEffect(() => {
    const fetchMedications = async () => {
      if (auth.currentUser) {
        try {
          const q = query(
            collection(db, "medications"),
            where("donorId", "==", auth.currentUser.uid)
          );
          const querySnapshot = await getDocs(q);
          const meds = [];
          querySnapshot.forEach((doc) => {
            meds.push({ id: doc.id, ...doc.data() });
          });
          setMedications(meds);
          if (meds.length === 0) {
            setAlert({ show: true, message: "No tienes medicamentos registrados.", variant: "info" });
          }
        } catch (error) {
          setAlert({ show: true, message: "Error al cargar los medicamentos.", variant: "danger" });
          console.error("Error al cargar los medicamentos: ", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchMedications();
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "medications", id));
      setMedications(medications.filter((med) => med.id !== id));
      setAlert({ show: true, message: "Medicamento eliminado correctamente.", variant: "success" });
    } catch (error) {
      setAlert({ show: true, message: "Error al eliminar el medicamento.", variant: "danger" });
      console.error("Error al eliminar el medicamento: ", error);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleEdit = (medication) => {
    setEditingId(medication.id);
    setEditedMedication({
      name: medication.name,
      dosage: medication.dosage,
      expirationDate: medication.expirationDate,
      quantity: medication.quantity,
      condition: medication.condition,
      category: medication.category,
      description: medication.description
    });
  };

  const handleSave = async (id) => {
    try {
      const medicationRef = doc(db, "medications", id);
      await updateDoc(medicationRef, editedMedication);
      setMedications(
        medications.map((med) =>
          med.id === id ? { ...med, ...editedMedication } : med
        )
      );
      setEditingId(null);
      setAlert({ show: true, message: "Medicamento actualizado correctamente.", variant: "success" });
    } catch (error) {
      setAlert({ show: true, message: "Error al actualizar el medicamento.", variant: "danger" });
      console.error("Error al actualizar el medicamento: ", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedMedication(prev => ({ ...prev, [name]: value }));
  };

  const confirmDelete = (id) => {
    setMedToDelete(id);
    setShowDeleteModal(true);
  };

  return (
    <div className="medication-list">
      <h2>Mis Medicamentos</h2>

      {alert.show && (
        <Alert variant={alert.variant} onClose={() => setAlert({ ...alert, show: false })} dismissible>
          {alert.message}
        </Alert>
      )}

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : medications.length === 0 ? (
        <p className="no-medications">No tienes medicamentos registrados.</p>
      ) : (
        <div className="medication-grid">
          {medications.map((med) => (
            <div key={med.id} className={`medication-card ${editingId === med.id ? 'editing' : ''}`}>
              {editingId === med.id ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>Nombre</label>
                    <input
                      type="text"
                      name="name"
                      value={editedMedication.name}
                      onChange={handleChange}
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Dosificación</label>
                      <input
                        type="text"
                        name="dosage"
                        value={editedMedication.dosage}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Cantidad</label>
                      <input
                        type="number"
                        name="quantity"
                        value={editedMedication.quantity}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Fecha Vencimiento</label>
                      <input
                        type="date"
                        name="expirationDate"
                        value={editedMedication.expirationDate}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>Condición</label>
                      <select
                        name="condition"
                        value={editedMedication.condition}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="">Seleccione...</option>
                        <option value="Nuevo">Nuevo</option>
                        <option value="Abierto">Abierto</option>
                        <option value="Sin abrir">Sin abrir</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Categoría</label>
                    <select
                      name="category"
                      value={editedMedication.category}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="">Seleccione...</option>
                      <option value="Analgésicos">Analgésicos</option>
                      <option value="Antibióticos">Antibióticos</option>
                      <option value="Antihistamínico">Antihistamínico</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Descripción</label>
                    <textarea
                      name="description"
                      value={editedMedication.description}
                      onChange={handleChange}
                      className="form-control"
                      rows="3"
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button onClick={() => handleSave(med.id)} className="btn-save">
                      <FaSave /> Guardar
                    </button>
                    <button onClick={handleCancelEdit} className="btn-cancel">
                      <FaTimes /> Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="card-header">
                    <h3>{med.name}</h3>
                    <div className="card-actions">
                      <button onClick={() => handleEdit(med)} className="btn-edit">
                        <FaEdit />
                      </button>
                      <button onClick={() => confirmDelete(med.id)} className="btn-delete">
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    <div className="info-row">
                      <span className="info-label">Dosificación:</span>
                      <span className="info-value">{med.dosage}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Cantidad:</span>
                      <span className="info-value">{med.quantity}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Vencimiento:</span>
                      <span className="info-value">{med.expirationDate}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Condición:</span>
                      <span className="info-value">{med.condition}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Categoría:</span>
                      <span className="info-value">{med.category}</span>
                    </div>
                    {med.description && (
                      <div className="info-row">
                        <span className="info-label">Descripción:</span>
                        <span className="info-value">{med.description}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de confirmación para eliminar */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro que deseas eliminar este medicamento? Esta acción no se puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={() => handleDelete(medToDelete)}>
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MedicationList;